import { Link, useRoute } from '@buzola/router'
import { type ReactNode, useEffect } from 'react'
import { adminSectionForPath, useAdminSectionAccess } from '../lib/section-access'
import { ConfirmDialogHost } from './confirm-dialog'
import { NavSidebar } from './nav-sidebar'
import { AdminToastContainer } from './toast'

/**
 * App shell: navy sidebar on the left, content area on the right.
 * Wraps the buzola <Outlet/> in application.tsx.
 *
 * `data-section` marks the active product area from the URL (same rule as
 * NavSidebar). It is the single boundary the GD brand override in index.css
 * hooks onto — SN routes render without it and keep the shared salmon palette.
 * The same flag is mirrored onto <body> so the GD override also reaches Radix
 * dialogs (PersistButton's dirty prompt, UnsavedChangesGuard), which portal to
 * document.body and so render outside this wrapper.
 */
export function AdminLayout({ children }: { children: ReactNode }) {
	const { pathname } = useRoute()
	const canAccess = useAdminSectionAccess()
	const section = pathname === '/gd' || pathname.startsWith('/gd/') ? 'gd' : 'sn'

	useEffect(() => {
		document.body.dataset.section = section
	}, [section])

	return (
		<div className="flex min-h-screen bg-bg-gray" data-section={section}>
			<NavSidebar />
			<main className="flex-1 min-w-0">
				<div className="max-w-screen-2xl mx-auto px-4 md:px-8 py-6 md:py-8">
					{canAccess(adminSectionForPath(pathname)) ? children : <SectionForbidden />}
				</div>
			</main>
			<ConfirmDialogHost />
			<AdminToastContainer />
		</div>
	)
}

/**
 * Stands in for a route the signed-in role cannot open — a deep link, a stale
 * bookmark. The page itself would otherwise render and fail on the first query
 * the ACL refuses.
 */
function SectionForbidden() {
	return (
		<div className="mx-auto max-w-lg rounded-lg border border-light-gray bg-white px-6 py-10 text-center">
			<h1 className="font-vag-normal text-blue-primary text-xl">Sem nemáš oprávnění</h1>
			<p className="mt-2 text-gray text-sm leading-relaxed">
				Tahle část administrace je vyhrazená správcům. Pokud ji potřebuješ, řekni si někomu, kdo spravuje uživatele.
			</p>
			<Link className="mt-4 inline-block text-orange text-sm hover:underline" to="cms/articles">
				Zpět do obsahu webu →
			</Link>
		</div>
	)
}
